// SessionService.js - Common session management utility

import AgentConfigService from './AgentConfigService';
import sessionStorageManager from './sessionStorageManager';
import { message } from 'antd';

class SessionService {
  

  // Update to SessionService.js logout method to properly clear chat history
  /**
   * Comprehensive logout function that can be used from any component
   * @param {string} sessionId - Current session ID
   * @param {function} onLogout - Parent component's logout callback (optional)
   * @param {function} navigate - React Router navigate function (optional)
   * @param {object} options - Additional options for logout behavior
   */
  static async logout(sessionId, onLogout = null, navigate = null, options = {}) {
    // Default options - define outside try block for proper scoping
    const {
      showSuccessMessage = true,
      navigateToRoot = true,
      callParentLogout = true
    } = options;
    
    try {
      console.log('Starting SessionService logout process...');
      
      // Get the main API URL
      const mainApiUrl = await AgentConfigService.getMainApiUrl();
      
      // STEP 1: Call backend logout endpoint if we have a session
      if (sessionId) {
        console.log('Calling backend logout endpoint...');
        try {
          // Don't await this - fire and forget to avoid blocking UI
          fetch(`${mainApiUrl}/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionId}`
            },
            credentials: 'include'
          }).catch(err => {
            console.log('Logout API error (non-blocking):', err);
          });
        } catch (err) {
          console.warn('Error calling logout endpoint:', err);
        }
      }
      
      // STEP 2: Clear session-specific data from sessionStorageManager
      if (sessionId) {
        console.log('Clearing session data from sessionStorageManager...');
        try {
          if (sessionStorageManager && typeof sessionStorageManager.clearSessionData === 'function') {
            const clearedSessionItems = sessionStorageManager.clearSessionData(sessionId);
            console.log(`Cleared ${clearedSessionItems} session-specific items`);
          } else {
            console.log('clearSessionData method not available, using manual session cleanup...');
            // Manual session cleanup
            const keys = Object.keys(localStorage);
            const sessionPrefix = `${sessionId}:`;
            let manualSessionCleared = 0;
            
            keys.forEach(key => {
              if (key.startsWith(sessionPrefix)) {
                try {
                  localStorage.removeItem(key);
                  manualSessionCleared++;
                } catch (e) {
                  console.warn(`Error removing session key ${key}:`, e);
                }
              }
            });
            console.log(`Manually cleared ${manualSessionCleared} session-specific items`);
          }
        } catch (err) {
          console.warn('Error clearing session data:', err);
        }
      }
      
      // STEP 3: Clear all chat history from localStorage
      try {
        console.log('Clearing chat history from localStorage...');
        const allKeys = Object.keys(localStorage);
        const chatHistoryKeys = allKeys.filter(key => key.startsWith('chat_history_'));
        let chatHistoryCleared = 0;
        
        chatHistoryKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
            chatHistoryCleared++;
          } catch (e) {
            console.warn(`Error removing chat history key ${key}:`, e);
          }
        });
        
        console.log(`Cleared ${chatHistoryCleared} chat history items`);
      } catch (err) {
        console.warn('Error clearing chat history:', err);
      }
      
      // STEP 4: Clear standard localStorage items
      const standardKeys = [
        'sessionId',
        'username',
        'userRole',
        'session_id'
      ];
      
      standardKeys.forEach(key => {
        try {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            console.log(`Removed standard key: ${key}`);
          }
        } catch (err) {
          console.warn(`Error removing ${key}:`, err);
        }
      });
      
      // STEP 5: Clear any legacy agent-specific data
      try {
        const allKeys = Object.keys(localStorage);
        const legacyKeys = allKeys.filter(key => 
          key.startsWith('last_workflow_') || 
          key.startsWith('agent_key_') ||
          key.startsWith('component_map_') ||
          key.startsWith('agent_state_') ||
          key.startsWith('workflow_') ||
          key.startsWith('session_')
        );
        
        legacyKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
            console.log(`Removed legacy key: ${key}`);
          } catch (err) {
            console.warn(`Error removing legacy key ${key}:`, err);
          }
        });
        
        console.log(`Cleaned up ${legacyKeys.length} legacy keys`);
      } catch (err) {
        console.warn('Error during legacy cleanup:', err);
      }
      
      // STEP 6: Clear all cookies
      try {
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
        console.log('Cleared all cookies');
      } catch (err) {
        console.warn('Error clearing cookies:', err);
      }
      
      // STEP 7: Clear sessionStorage
      try {
        sessionStorage.clear();
        console.log('Cleared sessionStorage');
      } catch (err) {
        console.warn('Error clearing sessionStorage:', err);
      }
      
      // STEP 8: Clear browser navigation history
      if (navigateToRoot) {
        try {
          // Clear the browser history by replacing the current entry with root
          window.history.replaceState({}, '', '/');
          
          // Clear any additional history entries (this clears the history stack)
          if (window.history.length > 1) {
            // Navigate to root and clear the stack
            window.history.go(-(window.history.length - 1));
          }
          
          console.log('Cleared browser navigation history');
        } catch (err) {
          console.warn('Error clearing navigation history:', err);
        }
      }
      
      // STEP 9: Call parent logout function or navigate
      if (callParentLogout && onLogout && typeof onLogout === 'function') {
        console.log('Calling parent onLogout function...');
        onLogout();
      } else if (navigate && typeof navigate === 'function') {
        console.log('Using navigate function to go to root...');
        navigate('/', { replace: true });
      } else if (navigateToRoot) {
        // Fallback: force navigate to root
        console.log('Using fallback navigation to root...');
        window.location.href = '/';
      }
      
      // STEP 10: Show success message
      if (showSuccessMessage) {
        message.success('Logged out successfully');
      }
      
      console.log('SessionService logout process completed successfully');
      return true;
      
    } catch (error) {
      console.error('SessionService logout error:', error);
      
      // Emergency cleanup - if all else fails, clear everything and reload
      console.log('Performing emergency logout...');
      
      try {
        // Nuclear option - clear all localStorage and reload
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear all cookies
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
        
        // Force page reload to ensure clean state
        if (navigateToRoot) {
          window.location.href = '/';
        }
        
      } catch (cleanupError) {
        console.error('Emergency cleanup error:', cleanupError);
        // Last resort
        window.location.reload();
      }
      
      message.warning('Logout completed with warnings. You have been logged out.');
      return false;
    }
  }  


  /**
   * Clear all storage data without logout (useful for login page cleanup)
   */
  static clearAllStorageData() {
    try {
      console.log('SessionService: Clearing all storage data...');
      
      // Clear sessionStorageManager data
      if (sessionStorageManager && typeof sessionStorageManager.clearAllSessionStorageManagerData === 'function') {
        const clearedCount = sessionStorageManager.clearAllSessionStorageManagerData();
        console.log(`Cleared ${clearedCount} sessionStorageManager items`);
      }
      
      // Clear standard session items
      const standardSessionKeys = [
        'sessionId',
        'username', 
        'userRole',
        'session_id'
      ];
      
      standardSessionKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          console.log(`Removed standard session key: ${key}`);
        }
      });
      
      // Clear legacy keys
      const legacyKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('last_workflow_') || 
        key.startsWith('agent_key_') ||
        key.startsWith('component_map_') ||
        key.startsWith('agent_state_')
      );
      
      legacyKeys.forEach(key => {
        localStorage.removeItem(key);
        console.log(`Removed legacy key: ${key}`);
      });
      
      // Clear all cookies
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      });
      
      // Clear sessionStorage
      try {
        sessionStorage.clear();
        console.log('Cleared sessionStorage');
      } catch (e) {
        console.warn('Error clearing sessionStorage:', e);
      }
      
      // Clear navigation history
      try {
        window.history.replaceState({}, '', '/');
        console.log('Cleared navigation history');
      } catch (e) {
        console.warn('Error clearing navigation history:', e);
      }
      
      console.log('SessionService: Storage cleanup completed');
      return true;
      
    } catch (error) {
      console.error('SessionService: Error during storage cleanup:', error);
      return false;
    }
  }
  
  /**
   * Check if user is logged in by verifying session data
   */
  static isLoggedIn() {
    const sessionId = localStorage.getItem('sessionId');
    const username = localStorage.getItem('username');
    return !!(sessionId && username);
  }
  
  /**
   * Get current session information
   */
  static getSessionInfo() {
    return {
      sessionId: localStorage.getItem('sessionId'),
      username: localStorage.getItem('username'),
      userRole: localStorage.getItem('userRole')
    };
  }
  
  /**
   * Initialize session after login
   */
  static initializeSession(username, sessionId, userRole) {
    try {
      // Store session information in local storage
      localStorage.setItem('sessionId', sessionId);
      localStorage.setItem('username', username);
      localStorage.setItem('userRole', userRole || 'testrole');
      
      // Set the session ID as a cookie as well for API requests
      document.cookie = `session_id=${sessionId}; path=/; max-age=28800`; // 8 hours
      
      // Initialize project system in session storage manager
      if (sessionStorageManager && typeof sessionStorageManager.setCurrentProjectId === 'function') {
        sessionStorageManager.setCurrentProjectId(sessionId, sessionStorageManager.DEFAULT_PROJECT_ID);
      }
      
      // Always navigate to home after login to ensure clean start
      window.history.replaceState({}, '', '/home');
      
      console.log('SessionService: Session initialized successfully');
      return true;
    } catch (error) {
      console.error('SessionService: Error initializing session:', error);
      return false;
    }
  }
}

export default SessionService;